import { useState, useEffect } from 'react'
import { Monitor, Moon, Sun } from 'lucide-react'

type Theme = 'system' | 'asm-dark' | 'asm-light'

const cycle: Theme[] = ['system', 'asm-dark', 'asm-light']

function resolveTheme(theme: Theme): 'asm-dark' | 'asm-light' {
  if (theme === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'asm-dark' : 'asm-light'
  }
  return theme
}

export default function ThemeToggle() {
  const [theme, setTheme] = useState<Theme>('system')

  useEffect(() => {
    const saved = (localStorage.getItem('theme') as Theme) || 'system'
    setTheme(saved)
    document.documentElement.setAttribute('data-theme', resolveTheme(saved))
  }, [])

  useEffect(() => {
    if (theme !== 'system') return
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = () =>
      document.documentElement.setAttribute('data-theme', resolveTheme('system'))
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [theme])

  const cycleTheme = () => {
    const next = cycle[(cycle.indexOf(theme) + 1) % cycle.length]
    setTheme(next)
    localStorage.setItem('theme', next)
    document.documentElement.setAttribute('data-theme', resolveTheme(next))
  }

  const Icon = theme === 'system' ? Monitor : theme === 'asm-dark' ? Moon : Sun
  const label =
    theme === 'system' ? 'System theme' : theme === 'asm-dark' ? 'Dark theme' : 'Light theme'

  return (
    <button
      onClick={cycleTheme}
      title={label}
      aria-label={label}
      className="h-9 w-9 rounded-md grid place-items-center opacity-70 hover:opacity-100 hover:bg-base-200 transition"
    >
      <Icon className="h-4 w-4" />
    </button>
  )
}
